import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { XClose } from '@untitled-ui/icons-react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

// How long after a frame loads a focus move into it counts as the frame's own
// (measured: Chrome's PDF viewer grabs it about 300 ms after load).
const FRAME_FOCUS_GRAB_MS = 2000

/**
 * Dialog shell of the file viewers (FileViewer, the Bulk Enquiry previews): a
 * portal over the page, closed by Escape, the close button or a click on the
 * backdrop. Not MyModal: its MUI focus trap fights an <iframe> (a PDF preview
 * could not be scrolled or zoomed). Focus moves into the dialog when it opens,
 * Tab stays inside it, and focus goes back to where it was when it closes.
 *
 * zIndex: above whatever opened it (FileViewer opens over sliders: 99999).
 * className: extra classes of the dialog box (a fixed height, for instance).
 */
export default function Overlay({
  open,
  onClose,
  title,
  subtitle,
  actions,
  maxWidth = 960,
  zIndex = 1400,
  className = '',
  children,
  footer,
}) {
  const dialog = useRef(null)
  // The latest onClose, so a parent re-render (a new function) does not
  // re-run the open effect and pull the focus away from a field.
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    if (!open) return undefined
    const previous = document.activeElement
    dialog.current?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        close.current?.()
        return
      }
      // Tab from the last element goes back to the first, Shift+Tab from the
      // first to the last; only while the focus is inside this dialog.
      const box = dialog.current
      if (e.key !== 'Tab' || !box || !box.contains(document.activeElement)) return
      const items = Array.from(box.querySelectorAll(FOCUSABLE))
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && (document.activeElement === first || document.activeElement === box)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    // Chrome's PDF viewer pulls the focus into its <iframe> on its own, a few
    // hundred ms after the frame loads, when it cannot show the file (a file
    // that is not really a PDF). Keys pressed in a frame never reach this
    // document, so Escape would stop closing the dialog: take the focus back
    // when it lands in a frame shortly after that frame loaded. Later on, a
    // frame only gets the focus from a click into it: that one is the user's.
    const box = dialog.current
    let frameLoadedAt = -Infinity
    const onFrameLoad = (e) => {
      if (e.target instanceof HTMLIFrameElement) frameLoadedAt = performance.now()
    }
    let refocus = null
    const onWindowBlur = () => {
      clearTimeout(refocus)
      refocus = setTimeout(() => {
        const active = document.activeElement
        if (!box || !(active instanceof HTMLIFrameElement) || !box.contains(active)) return
        if (performance.now() - frameLoadedAt <= FRAME_FOCUS_GRAB_MS) box.focus()
      }, 0)
    }
    // `load` does not bubble: listen in the capture phase.
    box?.addEventListener('load', onFrameLoad, true)
    window.addEventListener('blur', onWindowBlur)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      clearTimeout(refocus)
      box?.removeEventListener('load', onFrameLoad, true)
      window.removeEventListener('blur', onWindowBlur)
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      if (previous && typeof previous.focus === 'function') previous.focus()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex, backgroundColor: 'rgba(12, 17, 29, 0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        style={{ maxWidth }}
        className={
          'flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl outline-none ' + className
        }
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
            {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
            >
              <XClose className="size-5" stroke="currentColor" />
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-auto">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body
  )
}

Overlay.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  maxWidth: PropTypes.number,
  zIndex: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
}
